/**
 * Learning Center task list (Overview). Presentational: parent passes merged `tasks` only.
 * Do not add API calls or completion persistence here — that lives in `TasklistContext` + services.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasklistLearningCenterPagePathFromTask } from '@/utils/tasklistPath';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import { applyTasklistPlaceholders } from '@/utils/tasklistPagePlaceholders';
import { formatLearningCenterHubCount } from '@/utils/learningCenterHubRows';
import type { LearningCenterHubProgressLabels } from '@/types/learningCenterHubJson';

export interface LearningCenterTask {
  id: number;
  title: string;
  description: string;
  taskType: 'anytime' | 'actionable';
  completed: boolean;
  order: number;
  /** secondary pages `/tasklist/page/{pageId}` → `public/data/pages/{pageId}.json` */
  pageId?: string;
  pathSegment?: string;
  group?: string;
  roles?: string[];
}

interface LearningCenterProps {
  tasks?: LearningCenterTask[];
  eyebrow?: string;
  title?: string;
  description?: string;
  onTaskClick?: (task: LearningCenterTask) => void;
  /** When true, show skeleton. When false with empty tasks and `emptyStateMessage`, show empty state instead of skeleton. */
  isLoading?: boolean;
  progressLabels?: LearningCenterHubProgressLabels;
  countFormatTemplate?: string;
  seeMoreLabel?: string;
  seeLessLabel?: string;
  emptyStateMessage?: string;
  /** Hub page: full-width list + theme card styling; hides left intro column */
  variant?: 'default' | 'hub';
  showSeeMoreOnDescriptions?: boolean;
  /** When false, hides the completion label, count, and progress bar (e.g. teacher preview). */
  showProgressBar?: boolean;
}

const DESCRIPTION_COLLAPSE_CHARS = 140;

const ChevronIcon: React.FC = () => (
  <svg
    className="lc-chevron-icon"
    width="28"
    height="28"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M7.5 5L12.5 10L7.5 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Complete / incomplete: Replit uses size-[44px] rounded-full; incomplete ring border 1px primary-dark.
const CompleteIcon: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 40 40" fill="none" aria-label="Completed">
    <rect className="lc-icon-complete-bg" width="40" height="40" rx="20" fill="currentColor" />
    <path
      d="M27 14.5L17.125 24.375L13 20.25"
      stroke="#FFFFFF"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IncompleteIcon: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-label="Incomplete">
    <circle className="lc-icon-incomplete-ring" cx="22" cy="22" r="21.5" fill="var(--white, #ffffff)" />
  </svg>
);

const SKELETON_COUNT = 5;

const LearningCenter: React.FC<LearningCenterProps> = ({
  tasks = [],
  eyebrow = 'LEARNING CENTER',
  title = 'Get up to speed before school starts',
  description = 'Get important information on what to expect before your learning journey begins.',
  onTaskClick,
  isLoading,
  progressLabels,
  countFormatTemplate = '{completed}/{total} complete',
  seeMoreLabel = 'See more',
  seeLessLabel = 'See less',
  emptyStateMessage,
  variant = 'default',
  showSeeMoreOnDescriptions,
  showProgressBar = true,
}) => {
  const navigate = useNavigate();
  const { selectedStudent } = useSelectedStudent();
  const [expandedDescIds, setExpandedDescIds] = useState<Record<number, boolean>>({});

  const placeholderVars = useMemo(
    () => ({
      studentName: selectedStudent?.nickName || selectedStudent?.firstName || 'your student',
      school: selectedStudent?.schoolShortName || selectedStudent?.schoolName || 'your school',
    }),
    [selectedStudent]
  );

  const showEmptyState = Boolean(emptyStateMessage?.trim()) && isLoading === false && tasks.length === 0;
  const loading =
    isLoading === true || (tasks.length === 0 && !showEmptyState && isLoading !== false);

  const sortedTasks = [...tasks].sort((a, b) => a.order - b.order);
  const completedCount = sortedTasks.filter((t) => t.completed).length;
  const totalCount = sortedTasks.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const defaultLabels = {
    none: "Let's get started!",
    partial: "You're on your way!",
    complete: 'All done!',
  };
  const labels = { ...defaultLabels, ...progressLabels };

  const progressLabel =
    completedCount === 0
      ? labels.none
      : completedCount === totalCount
        ? labels.complete
        : labels.partial;

  const countDisplay =
    totalCount > 0 ? formatLearningCenterHubCount(countFormatTemplate, completedCount, totalCount) : '';

  const handleTaskClick = (task: LearningCenterTask) => {
    if (onTaskClick) {
      onTaskClick(task);
      return;
    }
    const pageId = task.pageId?.trim();
    if (pageId && /^[a-zA-Z0-9_-]+$/.test(pageId)) {
      navigate(tasklistLearningCenterPagePathFromTask(task));
      return;
    }
    navigate(`/learning-center/${task.id}`);
  };

  const rootClass = variant === 'hub' ? 'learning-center learning-center--hub' : 'learning-center';
  const showProgressBlock = showProgressBar && !loading && totalCount > 0;
  const effectiveSeeMore = showSeeMoreOnDescriptions ?? variant === 'hub';

  const toggleDescExpanded = (id: number) => {
    setExpandedDescIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className={rootClass}>
      {variant !== 'hub' && (
        <div className="lc-info">
          <span className="learning-materials-section__eyebrow">{eyebrow}</span>
          <h2 className="lc-title">{title}</h2>
          <p className="lc-description">{description}</p>
        </div>
      )}

      <div className="lc-task-panel">
        {showProgressBlock && (
          <div className="lc-progress-block">
            <div className="lc-progress-header">
              <span className="lc-progress-label">{progressLabel}</span>
              <span className="lc-progress-count">{countDisplay}</span>
            </div>

            <div
              className="lc-progress-bar-track"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="lc-progress-bar-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {showProgressBar && loading && (
          <div className="lc-progress-block">
            <div className="lc-progress-header">
              <span className="lc-progress-label">
                <span
                  className="placeholder placeholder-glow rounded"
                  style={{ width: 120, height: 16, display: 'inline-block' }}
                />
              </span>
              <span className="lc-progress-count">
                <span
                  className="placeholder placeholder-glow rounded"
                  style={{ width: 80, height: 16, display: 'inline-block' }}
                />
              </span>
            </div>
            <div className="lc-progress-bar-track" role="progressbar" aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}>
              <span className="placeholder placeholder-glow rounded-pill" style={{ width: '100%', height: 'var(--lc-progress-bar-track-height, 16px)', display: 'block' }} />
            </div>
          </div>
        )}

        {showEmptyState && (
          <p className="lc-empty-state" role="status">
            {emptyStateMessage}
          </p>
        )}

        <ul className="lc-task-list">
          {loading
            ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <li className="lc-task-item lc-task-item--skeleton" key={index}>
                  <div className="d-flex align-items-center gap-3 w-100">
                    <div
                      className="placeholder placeholder-glow rounded-circle flex-shrink-0"
                      style={{ width: 44, height: 44 }}
                    />
                    <div className="d-flex flex-column gap-2 flex-grow-1">
                      <div
                        className="placeholder placeholder-glow rounded"
                        style={{ height: 16, width: '60%' }}
                      />
                      <div
                        className="placeholder placeholder-glow rounded"
                        style={{ height: 12, width: '40%' }}
                      />
                    </div>
                    <div
                      className="placeholder placeholder-glow rounded flex-shrink-0"
                      style={{ width: 20, height: 20 }}
                    />
                  </div>
                </li>
              ))
            : sortedTasks.map((task) => {
                const displayTitle = applyTasklistPlaceholders(task.title, placeholderVars);
                const fullDescription = applyTasklistPlaceholders(task.description, placeholderVars);
                const expanded = Boolean(expandedDescIds[task.id]);
                const needsTruncate = effectiveSeeMore && fullDescription.length > DESCRIPTION_COLLAPSE_CHARS;
                const displayDescription =
                  needsTruncate && !expanded
                    ? `${fullDescription.slice(0, DESCRIPTION_COLLAPSE_CHARS).trimEnd()}…`
                    : fullDescription;

                return (
                  <li
                    key={task.id}
                    className={`lc-task-item ${task.completed ? 'lc-task-item--completed' : ''}`}
                    onClick={() => handleTaskClick(task)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTaskClick(task);
                      }
                    }}
                    aria-label={`${displayTitle}${task.completed ? ' (completed)' : ''}`}
                  >
                    <span
                      className={`lc-task-icon ${
                        task.completed ? 'lc-task-icon--complete' : 'lc-task-icon--incomplete'
                      }`}
                      data-testid="lc-task-icon"
                    >
                      {task.completed ? <CompleteIcon /> : <IncompleteIcon />}
                    </span>

                    <span className="lc-task-body">
                      <span className="lc-task-title">{displayTitle}</span>
                      <span className="lc-task-desc">
                        {displayDescription}
                        {needsTruncate && (
                          <button
                            type="button"
                            className="lc-desc-toggle btn btn-link p-0 align-baseline"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDescExpanded(task.id);
                            }}
                          >
                            {expanded ? seeLessLabel : seeMoreLabel}
                          </button>
                        )}
                      </span>
                    </span>

                    <ChevronIcon />
                  </li>
                );
              })}
        </ul>
      </div>
    </div>
  );
};

export default LearningCenter;
